/**
 * Personal targets (spec section 21).
 *
 * Every field is optional and stored only in this browser. The page states
 * plainly that these are the user's own numbers — the app neither recommends
 * targets nor gives dietary advice.
 */

import { useState } from 'react';
import { EMPTY_TARGETS, useAppState, type Targets } from '@/state/AppState';
import { Notice } from '@/components/Panels';

const FIELDS: Array<{ key: keyof Targets; label: string; unit: string; hint: string }> = [
  { key: 'dailyKcal', label: 'Daily calorie target', unit: 'kcal', hint: 'Used to show what share of your day a package covers.' },
  { key: 'dailyProtein', label: 'Daily protein target', unit: 'g', hint: 'Used to show a product’s contribution toward your protein goal.' },
  { key: 'maxSpend', label: 'Maximum spend per item', unit: '', hint: 'A budget ceiling you can filter against.' },
  { key: 'minProtein', label: 'Minimum protein per 100 g', unit: 'g', hint: 'A default floor when filtering results.' },
  { key: 'maxKcal', label: 'Maximum calories per 100 g', unit: 'kcal', hint: 'A default ceiling when filtering results.' },
];

export function SettingsPage(): React.JSX.Element {
  const { targets, setTargets } = useAppState();
  const [draft, setDraft] = useState<Targets>(targets);
  const [saved, setSaved] = useState(false);

  const update = (key: keyof Targets, raw: string): void => {
    setSaved(false);
    setDraft({ ...draft, [key]: raw.trim() === '' ? null : Number(raw) });
  };

  const invalid = FIELDS.some((f) => {
    const v = draft[f.key];
    return v !== null && (!Number.isFinite(v) || v <= 0);
  });

  return (
    <>
      <h1 style={{ marginBottom: '0.75rem' }}>Your targets</h1>

      <Notice kind="info">
        These are optional and stored only in this browser. KcalTrack uses them to show how much a
        product contributes toward numbers <em>you</em> chose. It does not recommend targets and
        does not provide medical or dietary advice.
      </Notice>

      <section className="panel">
        {FIELDS.map((field) => (
          <div className="filter-group" key={field.key}>
            <label className="filter-group-title" htmlFor={`target-${field.key}`}>
              {field.label} {field.unit && `(${field.unit})`}
            </label>
            <input
              id={`target-${field.key}`}
              className="range-input"
              style={{ width: '100%' }}
              type="number"
              min={0}
              step="any"
              inputMode="decimal"
              placeholder="Not set"
              value={draft[field.key] ?? ''}
              onChange={(e) => update(field.key, e.target.value)}
            />
            <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)', margin: '0.25rem 0 0' }}>
              {field.hint}
            </p>
          </div>
        ))}

        {invalid && (
          <Notice kind="error">Targets must be positive numbers, or left blank.</Notice>
        )}

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={invalid}
            onClick={() => {
              setTargets(draft);
              setSaved(true);
            }}
          >
            Save targets
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => {
              setDraft(EMPTY_TARGETS);
              setTargets(EMPTY_TARGETS);
              setSaved(true);
            }}
          >
            Clear all
          </button>
        </div>

        {saved && (
          <p style={{ marginTop: '0.6rem', color: 'var(--good)', fontSize: '0.85rem' }} role="status">
            Saved.
          </p>
        )}
      </section>
    </>
  );
}
